/**
 * Section Promo Manager - Moon Moon Agency
 * Section MM-Section-Promo : sélecteur de taille unique qui met à jour le
 * prix, le prix barré, le badge de réduction et l'URL du CTA à partir du
 * lookup JSON pré-calculé côté serveur (aucun matching de variante en JS).
 */

/*********************************************************************************************
 * 🛏️ SECTION PROMO MANAGER
 */
const SectionPromoManager = {
	config: {
		selectors: {
			section: '[data-section-promo]',
			sizeData: '[data-promo-size-data]',
			sizeValue: '.mm-dd [data-dd-value]',
			price: '[data-promo-price]',
			compare: '[data-promo-compare]',
			discount: '[data-promo-discount]',
			discountText: '[data-promo-discount-text]',
			cta: '[data-promo-cta]',
			card: '.mm-section-promo__card',
			truncated: '[data-promo-truncated]',
		},
	},

	init() {
		const sections = window.getElements(this.config.selectors.section);
		if (!sections.length) return; // Guard clause

		// L'observateur doit viser le trigger définitif : quand la carte est
		// rechargée, celui posé avant le remplacement surveillerait un noeud
		// détaché du document.
		sections.forEach((section) => {
			this._restoreTruncatedCard(section).then(() => this._observeSize(section));
		});

		window.log('info', '🛏️ [SECTION_PROMO]', 'Initialized', { sections: sections.length });
	},

	/**
	 * Recharge la carte depuis un contexte neutre quand Shopify a tronqué les
	 * variantes du produit.
	 *
	 * Sur une page de collection construite sur une condition de variante (ex.
	 * « Matelas 90x190 »), Shopify n'expose que les variantes correspondant à
	 * cette condition — mais uniquement pour les produits MEMBRES de la
	 * collection. Le matelas du pack y perd donc ses autres tailles, et la
	 * section n'a plus qu'une taille à proposer alors qu'elle doit afficher la
	 * même chose partout (elle est pilotée par le metaobject du pack, pas par
	 * la collection). Aucun contournement n'existe côté Liquid : all_products
	 * subit le même filtrage.
	 *
	 * La Section Rendering API, elle, rend la section dans le contexte de l'URL
	 * demandée : appelée sur '/', il n'y a pas de collection courante, donc plus
	 * de filtrage. On ne remplace que la carte (le reste de la section est déjà
	 * correct) et seulement quand le Liquid a détecté la troncature.
	 */
	async _restoreTruncatedCard(section) {
		const s = this.config.selectors;
		const card = section.querySelector(s.truncated);
		if (!card) return;

		const sectionId = card.dataset.promoTruncated;
		try {
			const response = await fetch(`/?section_id=${encodeURIComponent(sectionId)}`);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
			const freshCard = doc.querySelector(s.card);
			if (!freshCard) throw new Error('carte absente de la réponse');

			freshCard.classList.add('mm-in');
			freshCard.removeAttribute('data-mm-reveal');
			card.replaceWith(freshCard);
			window.MmSizeSelector?.init(freshCard);
			// Les descendants arrivent avec leur data-mm-reveal, mais le scan de
			// mm-motion a déjà eu lieu : sans ce re-scan ils resteraient invisibles.
			window.MmMotion?.scan(freshCard);

			window.log('info', '🛏️ [SECTION_PROMO]', 'Carte rechargée hors contexte collection', { sectionId });
		} catch (error) {
			window.log('warn', '🛏️ [SECTION_PROMO]', 'Rechargement de la carte impossible, taille unique conservée', { error: error.message });
		}
	},

	/**
	 * Observe la valeur affichée dans le trigger du sélecteur plutôt que d'écouter
	 * le clic sur les options : en mode sheet mobile, mm-size-selector.js déplace
	 * le panneau (et donc les options) sur <body> — hors de la section, ce qui
	 * rendait toute délégation depuis la section aveugle au choix mobile. Le
	 * trigger, lui, ne bouge jamais, et sa valeur est la source de vérité de ce
	 * que le composant a réellement appliqué (clic desktop comme confirmation
	 * mobile).
	 */
	_observeSize(section) {
		if (section._promoSizeObserver) return;

		const valueEl = section.querySelector(this.config.selectors.sizeValue);
		if (!valueEl) return;

		const observer = new MutationObserver(() => this._selectSize(section, valueEl.textContent.trim()));
		observer.observe(valueEl, { childList: true, characterData: true, subtree: true });
		section._promoSizeObserver = observer;
	},

	/**
	 * Applique la taille choisie : prix, prix barré, badge %, CTA.
	 * Le reflet visuel du sélecteur (libellé du trigger, badge, fermeture du
	 * panneau, état aria) est assuré par mm-size-selector.js — ce module ne
	 * traite que les conséquences métier du changement de taille.
	 * @param {HTMLElement} section - La section promo concernée
	 * @param {string} value - La taille appliquée par le sélecteur
	 */
	_selectSize(section, value) {
		const s = this.config.selectors;
		const dataScript = section.querySelector(s.sizeData);
		if (!dataScript) return;

		let sizeData;
		try {
			sizeData = JSON.parse(dataScript.textContent);
		} catch (error) {
			window.log('error', '🛏️ [SECTION_PROMO]', 'Invalid size data JSON', { error: error.message });
			return;
		}

		const entry = sizeData[value];
		if (!entry) return;

		section.querySelectorAll(s.price).forEach((priceEl) => {
			priceEl.textContent = entry.price_formatted;
		});

		section.querySelectorAll(s.compare).forEach((compareEl) => {
			compareEl.textContent = entry.compare_price_formatted;
			compareEl.hidden = !entry.has_discount;
		});

		// Deux badges (carte desktop + contenu mobile), d'où querySelectorAll.
		section.querySelectorAll(s.discount).forEach((discountEl) => {
			const template = discountEl.dataset.percentageTemplate || '';
			const text = discountEl.querySelector(s.discountText);
			if (text && template) text.textContent = template.replace('__PERCENTAGE__', entry.percentage);
			discountEl.hidden = !entry.has_discount;
		});

		const ctaEl = section.querySelector(s.cta);
		if (ctaEl && entry.url) ctaEl.setAttribute('href', entry.url);

		window.emit('global:section-promo-size-changed', { value });
		window.log('info', '🛏️ [SECTION_PROMO]', 'Size changed', { value });
	},
};

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SectionPromoManager.init());
} else {
	SectionPromoManager.init();
}

document.addEventListener('shopify:section:load', () => SectionPromoManager.init());
